/**
 * Fase B / B3 — Propostas: regras e cálculos APROVADOS na DEMO-03 (tag
 * demo-03-aprovada), portados sem redesenho de frontend/prototipo/src
 * (dados.ts, precos.ts, condicoes.ts, ptax.ts, resultado.ts, cotacoes.ts e
 * acoes.ts). Usado pelo SERVIDOR (validação, pendências de envio, totais) e
 * pela TELA (mesmos cálculos): uma única fonte.
 *
 * O documento da versão (`Versao`) mantém os nomes de campo da DEMO-03. As
 * referências (produtos, parceiros e entidades, entidades e contatos do
 * Cliente) chegam numa `Base` montada a partir do banco.
 *
 * Diferenças em relação à DEMO-03 (registradas em B3_RESULTADO):
 *  - PTAX: sem a consulta oficial (P-21 pendente), a PTAX de cada cotação em
 *    moeda estrangeira é informada manualmente (origem, data de referência e
 *    justificativa); `oficial` passa a ser opcional;
 *  - "hoje", autor e data e hora vêm do servidor/sessão, não de constantes;
 *  - condições de pagamento e motivos da perda vêm de listas parametrizadas.
 */
import type { RegraCobrancaLocacao } from './locacao.ts';
import type { RegraRecebimentoCongelada } from './regra-recebimento.ts';
import { textoReajuste, type Reajuste } from './comercial.ts';

export type Tipo = 'venda' | 'locacao' | 'trade_in';
export type Moeda = 'BRL' | 'USD';
export type SituacaoVersao = 'rascunho' | 'enviada' | 'em_negociacao' | 'aceita' | 'aceita_parcialmente' | 'recusada' | 'expirada' | 'substituida';
export type Resultado = 'pendente' | 'aceito' | 'aceito_parcial' | 'nao_aceito';
export const SITUACOES_VERSAO: Record<SituacaoVersao, string> = {
  rascunho: 'Rascunho',
  enviada: 'Enviada',
  em_negociacao: 'Em negociação',
  aceita: 'Aceita',
  aceita_parcialmente: 'Aceita parcialmente',
  recusada: 'Recusada',
  expirada: 'Expirada',
  substituida: 'Substituída',
};
export const RESULTADOS: Record<Resultado, string> = { pendente: 'Pendente', aceito: 'Aceito', aceito_parcial: 'Aceito parcial', nao_aceito: 'Não aceito' };

/* ======================= Referências (Base) ======================= */
export interface Endereco {
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
}
export interface ProdutoRef {
  id: string;
  sku: string;
  nome: string;
  fabricante: string;
  unidade: string;
  moeda: Moeda;
  categoria: string;
  pilar: string;
  tipoItem: string;
  situacao: 'Ativo' | 'Inativo';
}
/** Cópia HISTÓRICA do produto guardada na versão enviada (alterar o cadastro depois não muda documentos antigos). */
export type ProdutoCopia = Pick<ProdutoRef, 'sku' | 'nome' | 'fabricante' | 'categoria' | 'pilar' | 'tipoItem' | 'unidade' | 'moeda'>;
export const copiaProduto = (p: ProdutoRef): ProdutoCopia => ({ sku: p.sku, nome: p.nome, fabricante: p.fabricante, categoria: p.categoria, pilar: p.pilar, tipoItem: p.tipoItem, unidade: p.unidade, moeda: p.moeda });
export interface CondicoesLocacaoRef {
  responsabilidades: string;
  implantacao: string;
  manutencao: string;
  substituicao: string;
  seguro: string;
  cancelamento: string;
  renovacao: string;
  reajuste: string;
  observacoes: string;
  reajustePadrao?: Reajuste;
  /** P3-28: regra de cobrança do parceiro (copiada para a versão com as condições); ausente = tudo "A confirmar" */
  regraCobranca?: RegraCobrancaLocacao;
}
/** Parceiro comercial (grupo). */
export interface GrupoRef {
  id: string;
  nome: string;
  papeis: string[];
  situacao: 'Ativo' | 'Inativo';
  comissaoPadrao?: { regra: IdRegraComissao; percentual: number };
  condicoesLocacao?: CondicoesLocacaoRef;
}
/** Entidade/CNPJ do parceiro. */
export interface EntidadeRef {
  id: string;
  grupoId: string;
  razao: string;
  fantasia: string;
  cnpj: string;
  uf: string;
  endereco: string;
  situacao: 'Ativa' | 'Inativa';
}
export interface EntidadeClienteRef {
  id: string;
  tipo: 'Matriz' | 'Filial' | 'Unidade' | 'Não informado';
  /** código CLI-NNNN/BBBB (0022) e se é a entidade principal do Cliente */
  codigo?: string | null;
  principal?: boolean;
  cnpj: string;
  razao: string;
  fantasia: string;
  ie?: string;
  ieSituacao?: string;
  endereco: Endereco;
  situacao: 'Ativa' | 'Inativa';
}
export interface ContatoRef {
  id: string;
  entidadeId: string;
  nome: string;
  cargo: string;
  email: string;
  telefone: string;
  situacao: 'Ativo' | 'Inativo';
  principal: boolean;
}
export interface ClienteRef {
  id: string;
  nome: string;
  entidades: EntidadeClienteRef[];
  contatos: ContatoRef[];
}
export interface Base {
  produtos: ProdutoRef[];
  grupos: GrupoRef[];
  entidades: EntidadeRef[];
  clientes: ClienteRef[];
}
export interface Registro {
  data: string; // AAAA-MM-DD ou AAAA-MM-DDTHH:MM
  texto: string;
  autor: string;
}

/* ======================= Comissão (Locação) ======================= */
export type IdRegraComissao = 'por_parcela' | 'mensal_projeto' | 'unica_projeto' | 'unica_venda';
export type BaseComissao = 'parcela' | 'total';
export const REGRAS_COMISSAO: { id: IdRegraComissao; nome: string; base: string; frequencia: string; baseLocacao?: BaseComissao }[] = [
  { id: 'por_parcela', nome: 'Recorrente por parcela', base: 'Valor de cada parcela paga pelo Cliente', frequencia: 'A cada parcela recebida', baseLocacao: 'parcela' },
  { id: 'mensal_projeto', nome: 'Recorrente mensalmente durante o projeto', base: 'Valor mensal do projeto', frequencia: 'Todo mês, durante o prazo', baseLocacao: 'parcela' },
  { id: 'unica_projeto', nome: 'Paga uma única vez sobre o total do projeto', base: 'Valor total do projeto', frequencia: 'Uma única vez', baseLocacao: 'total' },
  { id: 'unica_venda', nome: 'Paga uma única vez sobre o total da venda', base: 'Valor total da venda', frequencia: 'Uma única vez' },
];
export const regraComissao = (id: IdRegraComissao) => REGRAS_COMISSAO.find((r) => r.id === id)!;
/** Regra de comissão APLICADA na Proposta: herdada do parceiro, alterável com autorização e auditoria; congelada na versão aceita. */
export interface RegraComissaoAplicada {
  regra: IdRegraComissao;
  percentual: number;
  origem: 'herdada do parceiro' | 'alterada com autorização';
  parceiroGrupoId: string;
  historico: Registro[];
}

/* ======================= Preços, margem e frete (precos.ts) ======================= */
export type Modalidade = 'CIF' | 'FOB';
export type PagadorFrete = 'incluso' | 'inttrix' | 'cliente';
export interface Frete {
  modalidade: Modalidade;
  pagador: PagadorFrete;
  valor: number;
  /** Entidade faturadora com a qual o frete foi cotado (opcional): serve só para SUGERIR a alocação. */
  entidadeId?: string;
}
export const PAGADORES: Record<PagadorFrete, string> = {
  incluso: 'Incluso no preço do fornecedor (CIF)',
  inttrix: 'Pago/absorvido pela Inttrix (FOB)',
  cliente: 'Pago pelo cliente (FOB)',
};
export const arred = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;
/** Venda unitária a partir do custo e da margem desejada (%), com arredondamento monetário. */
/** Pacote 3 · P3-18: margem desejada inicial dos itens de propostas NOVAS (editável; itens existentes não são recalculados). */
export const MARGEM_DESEJADA_PADRAO = 7;

export function vendaPelaMargem(custo: number, margemDesejadaPct: number): number {
  if (margemDesejadaPct >= 100 || margemDesejadaPct < 0) return NaN;
  return arred(custo / (1 - margemDesejadaPct / 100));
}
/** Margem efetiva (%) sobre a VENDA. */
export const margemPct = (venda: number, custo: number) => (venda > 0 ? ((venda - custo) / venda) * 100 : 0);
/** Normaliza o frete para nunca ter combinação contraditória. */
export function normalizarFrete(f: Frete): Frete {
  if (f.modalidade === 'CIF') return { modalidade: 'CIF', pagador: 'incluso', valor: 0 };
  const n: Frete = { modalidade: 'FOB', pagador: f.pagador === 'cliente' ? 'cliente' : 'inttrix', valor: Math.max(0, arred(Number(f.valor) || 0)) };
  return f.entidadeId ? { ...n, entidadeId: f.entidadeId } : n;
}
export interface Linha {
  quantidade: number;
  custoUnit: number;
  vendaUnit: number;
}
export function economiaLinha(l: Linha) {
  const custoTotal = arred(l.custoUnit * l.quantidade);
  const vendaTotal = arred(l.vendaUnit * l.quantidade);
  const margem = arred(vendaTotal - custoTotal);
  return { custoTotal, vendaTotal, margem, margemPct: margemPct(vendaTotal, custoTotal) };
}
const CIF: Frete = { modalidade: 'CIF', pagador: 'incluso', valor: 0 };
/** Resumo econômico com os três cenários de frete. */
export function economia(linhas: Linha[], freteBruto: Frete = CIF) {
  const frete = normalizarFrete(freteBruto);
  const custo = arred(linhas.reduce((s, l) => s + economiaLinha(l).custoTotal, 0));
  const totalProdutos = arred(linhas.reduce((s, l) => s + economiaLinha(l).vendaTotal, 0));
  const bruta = arred(totalProdutos - custo);
  const freteDescontado = frete.pagador === 'inttrix' ? frete.valor : 0;
  const freteAoCliente = frete.pagador === 'cliente' ? frete.valor : 0;
  const aposFrete = arred(bruta - freteDescontado);
  const totalFinal = arred(totalProdutos + freteAoCliente);
  return { custo, totalProdutos, venda: totalProdutos, bruta, percentual: totalProdutos ? bruta / totalProdutos : 0, frete, freteDescontado, freteAoCliente, aposFrete, totalFinal, aFaturar: totalFinal };
}

/* ======================= Condições de pagamento e Locação (condicoes.ts) ======================= */
export interface CondicaoPagamento {
  id: string;
  nome: string;
  dias: number[];
}
const somaDias = (iso: string, dias: number) => new Date(Date.parse(`${iso}T00:00:00Z`) + dias * 86_400_000).toISOString().slice(0, 10);
const cent = (v: number) => Math.round(v * 100) / 100;
/** Prévia das parcelas (ilustrativa): divide o total igualmente; a última absorve o arredondamento. */
export function parcelasDaCondicao(lista: CondicaoPagamento[], id: string, total: number, dataBase: string) {
  const c = lista.find((x) => x.id === id);
  if (!c || total <= 0 || !c.dias.length) return [];
  const base = cent(total / c.dias.length);
  return c.dias.map((d, i) => ({ n: i + 1, vencimento: somaDias(dataBase, d), valor: i === c.dias.length - 1 ? cent(total - base * (c.dias.length - 1)) : base }));
}
/** Parcelas pelos prazos CONGELADOS (mesma divisão da prévia: partes iguais, resíduo na última). */
export const parcelasDosPrazos = (prazos: Pick<PrazosCongelados, 'dias'>, total: number, dataBase: string) => parcelasDaCondicao([{ id: 'congelada', nome: '', dias: prazos.dias }], 'congelada', total, dataBase);
export const PERIODICIDADES = ['Mensal', 'Trimestral', 'Semestral', 'Anual'] as const;
export type Periodicidade = (typeof PERIODICIDADES)[number];
export const MESES_POR_PERIODO: Record<Periodicidade, number> = { Mensal: 1, Trimestral: 3, Semestral: 6, Anual: 12 };
export const cobrancasDerivadas = (prazoMeses: number, periodicidade: Periodicidade) => prazoMeses / MESES_POR_PERIODO[periodicidade];
/** Cronograma ilustrativo da Locação: vencimento inicial + periodicidade; dia de cobrança, quando informado. */
export function cronogramaLocacao(vencimentoInicial: string, periodicidade: Periodicidade, parcelas: number, diaCobranca?: number) {
  const [a, m] = vencimentoInicial.split('-').map(Number);
  const dia = diaCobranca && diaCobranca >= 1 && diaCobranca <= 28 ? diaCobranca : Number(vencimentoInicial.slice(8, 10));
  return Array.from({ length: Math.max(0, parcelas) }, (_, i) => {
    const t = m - 1 + i * MESES_POR_PERIODO[periodicidade];
    const ano = a + Math.floor(t / 12);
    const mes = (t % 12) + 1;
    return { n: i + 1, vencimento: `${ano}-${String(mes).padStart(2, '0')}-${String(Math.min(dia, 28)).padStart(2, '0')}` };
  });
}
export const fimEstimado = (dataInicial: string, prazoMeses: number) => {
  const [a, m, d] = dataInicial.split('-').map(Number);
  const t = m - 1 + prazoMeses;
  return `${a + Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}-${String(Math.min(d, 28)).padStart(2, '0')}`;
};

/* ---------- Locação: preço FECHADO pelo parceiro (a comissão já está dentro) ---------- */
export function calcularLocacao(o: { valorParcela: number; prazoMeses: number; periodicidade: Periodicidade; percentual: number }, base: BaseComissao) {
  const erros: string[] = [];
  const passo = MESES_POR_PERIODO[o.periodicidade] ?? 1;
  const cobrancas = o.prazoMeses / passo;
  if (!(o.valorParcela > 0)) erros.push('Valor da parcela deve ser maior que zero.');
  if (!(o.prazoMeses >= 1) || !Number.isInteger(o.prazoMeses)) erros.push('Prazo contratual deve ser um número inteiro de meses a partir de 1.');
  else if (!Number.isInteger(cobrancas)) erros.push(`Prazo de ${o.prazoMeses} meses não é múltiplo da periodicidade ${String(o.periodicidade).toLowerCase()} (${passo} meses).`);
  if (!(o.percentual >= 0 && o.percentual < 100)) erros.push('Percentual de comissão deve estar entre 0 e 100.');
  const n = Math.max(1, Math.floor(cobrancas) || 1);
  const clienteParcela = arred(o.valorParcela);
  const totalContrato = arred(clienteParcela * n);
  const comissaoTotal = base === 'parcela' ? arred(arred((clienteParcela * o.percentual) / 100) * n) : arred((totalContrato * o.percentual) / 100);
  const comissaoParcela = base === 'parcela' ? arred((clienteParcela * o.percentual) / 100) : arred(comissaoTotal / n);
  return { erros, cobrancas: n, clienteParcela, totalContrato, comissaoParcela, comissaoTotal, liquidoParcela: arred(clienteParcela - comissaoParcela), liquidoTotal: arred(totalContrato - comissaoTotal) };
}

/* ---------- Trade-in: o comprador avalia CADA LINHA ---------- */
export type ModoComissao = 'percentual' | 'valor';
export interface PrecoLinhaTradein {
  unitBruto: number;
  unitCliente: number;
}
export function calcularTradein(a: { modo: ModoComissao; percentual: number; linhas: Record<string, PrecoLinhaTradein> }, equipamentos: { id: string; descricao: string; quantidade: number }[]) {
  const erros: string[] = [];
  if (a.modo === 'percentual' && !(a.percentual >= 0 && a.percentual < 100)) erros.push('Percentual de comissão deve estar entre 0 e 100.');
  const linhas = equipamentos.map((e) => {
    const p = a.linhas[e.id];
    if (!(e.quantidade > 0) || !Number.isInteger(e.quantidade)) erros.push(`${e.descricao}: quantidade deve ser um inteiro maior que zero.`);
    if (!p) erros.push(`${e.descricao}: sem valor avaliado pelo comprador.`);
    const unitBruto = arred(p?.unitBruto ?? 0);
    const unitCliente = a.modo === 'percentual' ? arred(unitBruto * (1 - a.percentual / 100)) : arred(p?.unitCliente ?? 0);
    if (unitBruto < 0 || unitCliente < 0) erros.push(`${e.descricao}: valores não podem ser negativos.`);
    if (unitCliente > unitBruto) erros.push(`${e.descricao}: o valor destinado ao Cliente não pode ser maior que a avaliação do comprador (comissão negativa).`);
    const totalBruto = arred(unitBruto * e.quantidade);
    const totalCliente = arred(unitCliente * e.quantidade);
    const comissao = arred(totalBruto - totalCliente);
    return { id: e.id, descricao: e.descricao, quantidade: e.quantidade, unitBruto, unitCliente, totalBruto, totalCliente, comissao, percentual: totalBruto > 0 ? (comissao / totalBruto) * 100 : 0 };
  });
  const bruto = arred(linhas.reduce((s, l) => s + l.totalBruto, 0));
  const oferta = arred(linhas.reduce((s, l) => s + l.totalCliente, 0));
  const comissao = arred(linhas.reduce((s, l) => s + l.comissao, 0));
  if (!(bruto > 0)) erros.push('A avaliação bruta total deve ser maior que zero.');
  if (comissao < 0) erros.push('A comissão total não pode ser negativa.');
  if (arred(oferta + comissao) !== bruto) erros.push('Total inconsistente: valor ao Cliente + comissão deve ser igual à avaliação do comprador.');
  return { erros, linhas, bruto, oferta, comissao, percentualEquivalente: bruto > 0 ? (comissao / bruto) * 100 : 0 };
}

/* ======================= PTAX por cotação (ptax.ts) ======================= */
export type OrigemPtaxManual = 'Distribuidor' | 'Ajuste manual' | 'Outra data de fechamento';
export const ORIGENS_PTAX: OrigemPtaxManual[] = ['Distribuidor', 'Ajuste manual', 'Outra data de fechamento'];
export interface PtaxOficial {
  moeda: 'USD';
  valor: number;
  tipo: 'venda';
  boletim: 'fechamento';
  dataReferencia: string;
  fonte: string;
  consultadaEm: string;
}
export interface PtaxManual {
  valor: number;
  origem: OrigemPtaxManual;
  dataReferencia: string;
  justificativa: string;
  usuario: string;
  alteradaEm: string;
}
/** PTAX da cotação: a oficial (quando houver consulta autorizada — P-21) e a substituição manual, com trilha. */
export interface PtaxCotacao {
  oficial?: PtaxOficial;
  manual?: PtaxManual;
  historico: Registro[];
}
export const novaPtax = (oficial?: PtaxOficial): PtaxCotacao => ({ ...(oficial ? { oficial: { ...oficial } } : {}), historico: [] });
export const ptaxUtilizada = (p?: PtaxCotacao) => (p ? (p.manual?.valor ?? p.oficial?.valor) : undefined);
export function validarPtaxManual(m: Partial<PtaxManual>, hoje?: string): string[] {
  const erros: string[] = [];
  if (!(typeof m.valor === 'number' && Number.isFinite(m.valor) && m.valor > 0)) erros.push('Informe uma PTAX maior que zero.');
  if (!m.origem || !ORIGENS_PTAX.includes(m.origem)) erros.push('Informe a origem da alteração.');
  if (!m.dataReferencia) erros.push('Informe a data de referência.');
  else if (hoje && m.dataReferencia > hoje) erros.push('A data de referência não pode ser futura.');
  if (!m.justificativa?.trim()) erros.push('A PTAX manual exige justificativa.');
  return erros;
}
const fmtPtax = (v?: number) => (v === undefined ? '—' : v.toLocaleString('pt-BR', { minimumFractionDigits: 4 }));
/** Aplica a substituição preservando o valor oficial e registrando a trilha. */
export function substituirPtax(p: PtaxCotacao, m: PtaxManual): PtaxCotacao {
  return { ...p, manual: m, historico: [...p.historico, { data: m.alteradaEm, texto: `PTAX ${p.manual || p.oficial ? 'substituída' : 'informada'}: ${fmtPtax(ptaxUtilizada(p))} → ${fmtPtax(m.valor)} (${m.origem}, referência ${m.dataReferencia}): ${m.justificativa}`, autor: m.usuario }] };
}
export function restaurarPtaxOficial(p: PtaxCotacao, usuario: string, quando: string): PtaxCotacao {
  if (!p.manual || !p.oficial) return p;
  const { manual: _m, ...resto } = p;
  return { ...resto, historico: [...p.historico, { data: quando, texto: `Voltou à PTAX oficial ${fmtPtax(p.oficial.valor)} (a manual ${fmtPtax(p.manual.valor)} fica no histórico)`, autor: usuario }] };
}

/* ======================= Documento da versão ======================= */
export interface ResultadoLinha {
  resultado: Resultado;
  quantidadeAceita: number;
  quantidadePerdida?: number;
  motivoPerda?: string;
  observacaoResultado?: string;
}
export interface Cotacao {
  id: string;
  produtoId: string;
  entidadeId: string;
  moeda: Moeda;
  custoUnitMoeda: number;
  prazoDias: number;
  observacao?: string;
  frete?: number;
  ptax?: PtaxCotacao;
}
export interface ItemProposta extends ResultadoLinha {
  id: string;
  produtoId: string;
  quantidade: number;
  entidadeId: string;
  custoUnitMoeda: number;
  margemDesejada: number;
  precoUnit: number;
  precoManual: boolean;
  disponibilidade: string;
  prazoDias: number;
  observacao: string;
  cotacaoId?: string;
  ptaxUtilizada?: number;
  produtoCopia?: ProdutoCopia;
}
export interface Entrega {
  mesmoFaturamento: boolean;
  entidadeId?: string;
  especifica?: { cnpj: string; endereco: Endereco };
  contato: string;
  telefone: string;
}
export interface ComponenteOperacao extends ResultadoLinha {
  id: string;
  descricao: string;
  categoria: string;
  fabricante: string;
  modelo: string;
  quantidade: number;
  condicao: string;
  ano: string;
  lote: string;
  pn?: string;
  produtoId?: string;
  pilar?: string;
  tipoItem?: string;
  observacaoTecnica?: string;
  serial?: string;
  processador?: string;
  hd?: string;
  storage?: string;
  memoria?: string;
  tela?: string;
  funcional?: string;
  acessorios?: string;
  observacoes?: string;
}
export const nomeComponente = (c: Pick<ComponenteOperacao, 'descricao' | 'categoria' | 'fabricante' | 'modelo'>) => c.descricao || [c.categoria, [c.fabricante, c.modelo].filter(Boolean).join(' ')].filter(Boolean).join(' — ') || 'Equipamento sem descrição';
export interface OfertaLocacao {
  id: string;
  entidadeId: string;
  valorParcela: number;
  periodicidade: Periodicidade;
  prazoMeses: number;
  percentual: number;
  reajusteIndice: string;
  reajustePeriodicidade: string;
  reajuste?: Reajuste;
  implantacao: string;
  validadeDias: number;
  observacao: string;
}
export interface DadosLocacao {
  descricao: string;
  quantidade: number;
  base: BaseComissao;
  dataInicial: string;
  vencimentoInicial: string;
  diaCobranca: number;
  formaPagamento: string;
  implantacao: string;
  observacoes: string;
  componentes: ComponenteOperacao[];
  ofertas: OfertaLocacao[];
  selecionadaId: string;
  condicoes?: CondicoesLocacaoRef & { origemGrupoId?: string; editadas?: boolean };
  reajuste?: Reajuste;
  reajusteOrigemGrupoId?: string;
  reajusteAjustado?: boolean;
  historicoReajuste?: Registro[];
  regraComissao?: RegraComissaoAplicada;
  dataImplantacao?: string;
  responsavelEntrega?: string;
  responsavelImplantacao?: string;
}
export interface AvaliacaoTradein {
  id: string;
  entidadeId: string;
  modo: ModoComissao;
  percentual: number;
  linhas: Record<string, PrecoLinhaTradein>;
  prazoRetiradaDias: number;
  logistica: string;
  validadeDias: number;
  condicoes: string;
}
export const RESPONSAVEIS_LOGISTICA = ['Comprador', 'Cliente', 'INTTRIX', 'Transportadora contratada', 'A definir'] as const;
export type ResponsavelLogistica = (typeof RESPONSAVEIS_LOGISTICA)[number];
export const MODALIDADES_LOGISTICA = ['Retirada no local', 'Entrega pelo Cliente', 'Logística reversa'] as const;
export type ModalidadeLogistica = (typeof MODALIDADES_LOGISTICA)[number];
export const RESPONSAVEIS_POR_MODALIDADE: Record<ModalidadeLogistica, ResponsavelLogistica[]> = {
  'Retirada no local': ['Comprador', 'INTTRIX', 'Transportadora contratada', 'A definir'],
  'Entrega pelo Cliente': ['Cliente', 'A definir'],
  'Logística reversa': ['Comprador', 'INTTRIX', 'Transportadora contratada', 'A definir'],
};
export interface RegistroImportacao {
  arquivo: string;
  assinatura: string;
  usuario: string;
  dataHora: string;
  validas: number;
  invalidas: number;
  duplicadas: number;
  ignoradas: number;
}
/**
 * Decisão (25/09/2026, alternativa B): os dados bancários NÃO são guardados no HUB; seguem por canal
 * apropriado fora dele. O HUB registra só a confirmação operacional (quem confirmou e quando), com auditoria.
 */
export interface ConfirmacaoBancaria {
  usuario: string;
  dataHora: string;
}
export interface DadosTradein {
  localParque: string;
  validadeAvaliacao: string;
  retirada: string;
  logistica: string;
  responsabilidades: string;
  condicoesOperacao: string;
  observacoes: string;
  equipamentos: ComponenteOperacao[];
  avaliacoes: AvaliacaoTradein[];
  selecionadaId: string;
  /** Definida só pelo servidor ("Confirmar dados bancários"); o rascunho da tela nunca a altera. */
  confirmacaoBancaria?: ConfirmacaoBancaria;
  localEntidadeId?: string;
  localOutro?: Endereco;
  responsavelLogistica?: ResponsavelLogistica;
  modalidadeLogistica?: ModalidadeLogistica | '';
  prazoRetirada?: string;
  preparacao?: string;
  contatoLocal?: { nome: string; telefone: string; email: string };
  agendamento?: string;
  importacoes?: RegistroImportacao[];
}
export interface CondicoesVersao {
  forma: string;
  condicaoId: string;
  condicao: string;
  prazoGeral: string;
  observacao: string;
  /** Pacote 4 (bloco 2): Trade-in — dias para o Cliente receber o valor destinado a ele (o recebimento é sempre por transferência bancária) */
  prazoRecebimentoDias?: number | null;
  /** Pacote 4 (bloco 2): Trade-in — evento a partir do qual o prazo conta (definido por negociação; sem padrão automático) */
  marcoRecebimento?: MarcoRecebimento | null;
  /** obrigatória quando o marco é "outro" */
  marcoRecebimentoDescricao?: string;
  /**
   * Pacote 5: prazos da condição de pagamento COPIADOS no envio (só Venda; o Pedido herda). O Contas a Receber calcula as
   * parcelas do Cliente só com eles, nunca com a lista atual. null = a condição não tem prazos estruturados (o calendário
   * do Cliente fica a informar no Financeiro). Ausente = versão enviada antes do Pacote 5.
   */
  prazos?: PrazosCongelados | null;
}
/** Prazos da condição de pagamento vigentes no envio da Proposta (cópia histórica; mudar o cadastro depois não os altera). */
export interface PrazosCongelados {
  condicaoId: string;
  nome: string;
  /** dias corridos contados da data da NF do parceiro, um por parcela */
  dias: number[];
  base: 'data_nf_parceiro';
  contagem: 'dias_corridos';
  /** partes iguais; o resíduo de arredondamento fica na última */
  divisao: 'partes_iguais_residuo_ultima';
  copiadoEm: string;
}
/** Cópia dos prazos da condição escolhida, no envio; sem dias cadastrados → null (calendário a informar no Financeiro). */
export function prazosDaCondicao(lista: CondicaoPagamento[], id: string, hoje: string): PrazosCongelados | null {
  const c = lista.find((x) => x.id === id);
  const dias = Array.isArray(c?.dias) ? c.dias.filter((d) => Number.isInteger(d) && d >= 0) : [];
  if (!c || !dias.length) return null;
  return { condicaoId: c.id, nome: c.nome, dias, base: 'data_nf_parceiro', contagem: 'dias_corridos', divisao: 'partes_iguais_residuo_ultima', copiadoEm: hoje };
}
export type MarcoRecebimento = 'coleta' | 'inspecao' | 'outro';
export const MARCOS_RECEBIMENTO: { id: MarcoRecebimento; nome: string }[] = [
  { id: 'coleta', nome: 'Coleta efetiva' },
  { id: 'inspecao', nome: 'Inspeção e aceite do comprador' },
  { id: 'outro', nome: 'Outro' },
];
/** Texto do marco: "Outro" mostra a descrição combinada. */
export const textoMarcoRecebimento = (c: Pick<CondicoesVersao, 'marcoRecebimento' | 'marcoRecebimentoDescricao'>) => (c.marcoRecebimento === 'outro' ? c.marcoRecebimentoDescricao?.trim() || 'Outro' : (MARCOS_RECEBIMENTO.find((m) => m.id === c.marcoRecebimento)?.nome ?? ''));
/** Pacote 4 (bloco 2): o recebimento do valor destinado ao Cliente no Trade-in é sempre por transferência bancária. */
export const RECEBIMENTO_TRADEIN = 'Transferência bancária';
/**
 * Texto único do recebimento do Cliente no Trade-in (Proposta, Pedido e documentos). Versões e Pedidos gravados ANTES
 * do prazo em dias (forma livre + condição da lista da Venda) aparecem como gravados, marcados como registro anterior.
 */
/**
 * P4 bloco 3 (I21): condição de pagamento exibida uma vez. O campo "forma" saiu da Proposta de Venda, mas o padrão
 * gravado ("Boleto") continuava concatenado com a condição ("Boleto Boleto 30 dias"). Mostra a condição válida; sem
 * condição, a forma gravada. Nada é regravado; Trade-in e Locação têm textos próprios e não usam esta função.
 */
export function pagamentoTexto(c: Pick<CondicoesVersao, 'forma' | 'condicao'>): string {
  const condicao = (c.condicao ?? '').trim();
  const forma = (c.forma ?? '').trim();
  return condicao || forma || '—';
}
export function recebimentoTradein(c: CondicoesVersao): { recebimento: string; prazo: string; marco: string; anterior: boolean } {
  if (typeof c.prazoRecebimentoDias === 'number') return { recebimento: RECEBIMENTO_TRADEIN, prazo: `${c.prazoRecebimentoDias} ${c.prazoRecebimentoDias === 1 ? 'dia' : 'dias'}`, marco: textoMarcoRecebimento(c) || '—', anterior: false };
  // registro anterior: sem marco nem prazo registrados — nada é atribuído
  return { recebimento: c.forma || '—', prazo: c.condicao || '—', marco: '', anterior: true };
}
/** Abatimento da comissão líquida estimada (interno), congelado no envio. */
export interface AbatimentoCongelado {
  percentual: number;
  vigenteDesde: string;
  /** o parâmetro não existia no envio: a coluna guarda 0 (restrição do banco), mas a tela mostra "não configurado" */
  naoConfigurado?: boolean;
}
/**
 * Abatimento a usar na comissão líquida estimada: o congelado na versão enviada ou, no rascunho, o vigente.
 * null = parâmetro comercial.abatimento_comissao não configurado — a tela mostra "não configurado" e não supõe percentual.
 */
export function abatimentoEfetivo(congelado: AbatimentoCongelado | undefined, vigente: { percentual: number; vigenteDesde: string } | null | undefined): AbatimentoCongelado | null {
  if (congelado) return congelado.naoConfigurado ? null : congelado;
  return vigente ?? null;
}
/** Documento da versão (formato da DEMO-03). Número, situação, datas e abatimento vêm das colunas da versão. */
export interface Versao {
  numero: number;
  situacao: SituacaoVersao;
  data: string;
  validade: string;
  enviadaEm?: string;
  entidadeClienteId: string;
  contatoId: string;
  responsavel: string; // id do usuário responsável INTTRIX
  faturamentoEntidadeId: string;
  entrega: Entrega;
  frete: Frete;
  condicoes: CondicoesVersao;
  termosVersao: string;
  ptax: number; // PTAX padrão da versão (item sem cotação selecionada; modelo anterior à PTAX da proposta)
  /**
   * PTAX ÚNICA da proposta (F1-57, aprovada em 09/10/2026): converte TODOS os itens em dólar desta versão. Presente =
   * versão criada no modelo novo (P5: documentos com uma PTAX só); ausente = modelo anterior (PTAX por cotação/item,
   * preservada como foi emitida). A PTAX própria de cada cotação fica só como histórico (P4).
   */
  ptaxProposta?: PtaxCotacao;
  itens: ItemProposta[];
  cotacoes: Cotacao[];
  locacao?: DadosLocacao;
  tradein?: DadosTradein;
  historicoCotacoes?: Registro[];
  abatimento?: AbatimentoCongelado;
  /** gravado no conteúdo no envio quando o parâmetro de abatimento não existia */
  abatimentoNaoConfigurado?: boolean;
  /** Congelados no ENVIO (definidos pelo servidor): termos comerciais e dados da INTTRIX usados no documento. */
  termos?: TermoComercial[];
  empresa?: DadosEmpresa | null;
  /** Termos Gerais vigentes no envio (só Proposta de Venda); ausente nas versões enviadas antes de existirem. */
  termosGerais?: TermosGerais;
  /**
   * Parametrização (24/10/2026): cópia, no ENVIO, da regra de recebimento da comissão de cada parceiro da operação
   * (por grupo do parceiro). Ausente nas versões enviadas antes; alterar o cadastro depois não muda esta cópia.
   */
  recebimentoParceiros?: Record<string, RegraRecebimentoCongelada>;
}
export interface Proposta {
  id: string;
  numero: string;
  oportunidadeId: string;
  tipo: Tipo;
  versoes: Versao[];
}

/* ======================= Leituras ======================= */
export const entidadeDe = (d: Pick<Base, 'entidades'>, id: string) => d.entidades.find((e) => e.id === id);
export const grupoDe = (d: Pick<Base, 'entidades' | 'grupos'>, entidadeId: string) => {
  const e = entidadeDe(d, entidadeId);
  return e ? d.grupos.find((g) => g.id === e.grupoId) : undefined;
};
export function entidadeClienteDe(d: Pick<Base, 'clientes'>, entidadeClienteId: string) {
  for (const c of d.clientes) {
    const e = c.entidades.find((x) => x.id === entidadeClienteId);
    if (e) return { cliente: c, entidade: e };
  }
  return undefined;
}
export const contatoDe = (d: Pick<Base, 'clientes'>, contatoId: string) => d.clientes.flatMap((c) => c.contatos).find((k) => k.id === contatoId);
export const produtoDe = (d: Pick<Base, 'produtos'>, id: string) => d.produtos.find((p) => p.id === id);
/** Dados do produto de um item: a CÓPIA histórica da versão (se houver) prevalece sobre o cadastro atual. */
export const produtoDoItem = (d: Pick<Base, 'produtos'>, i: { produtoId: string; produtoCopia?: ProdutoCopia }): ProdutoCopia => {
  if (i.produtoCopia) return i.produtoCopia;
  const p = produtoDe(d, i.produtoId);
  return p ? copiaProduto(p) : { sku: '', nome: 'Produto não encontrado', fabricante: '', categoria: '', pilar: '', tipoItem: '', unidade: '', moeda: 'BRL' };
};
export const enderecoTexto = (e: Endereco) => `${e.logradouro}, ${e.numero}${e.complemento ? ` — ${e.complemento}` : ''} — ${e.bairro} — ${e.cidade}/${e.uf} — CEP ${e.cep}`;
/** Versão VIGENTE: a última versão já emitida (não rascunho, não substituída); se só existe rascunho, ele. */
export function versaoVigente<V extends Pick<Versao, 'situacao'>>(p: { versoes: V[] }): V {
  const emitidas = p.versoes.filter((v) => v.situacao !== 'rascunho' && v.situacao !== 'substituida');
  return emitidas.at(-1) ?? p.versoes.at(-1)!;
}
export const rascunhoDe = <V extends Pick<Versao, 'situacao'>>(p: { versoes: V[] }) => p.versoes.find((v) => v.situacao === 'rascunho');
export const tipoDaVersao = (v: Pick<Versao, 'locacao' | 'tradein'>): Tipo => (v.locacao ? 'locacao' : v.tradein ? 'trade_in' : 'venda');
export const ofertaSelecionada = (v: Pick<Versao, 'locacao'>) => v.locacao?.ofertas.find((o) => o.id === v.locacao!.selecionadaId);
export const avaliacaoSelecionada = (v: Pick<Versao, 'tradein'>) => v.tradein?.avaliacoes.find((a) => a.id === v.tradein!.selecionadaId);
export const comNome = <T extends ComponenteOperacao>(eqs: T[]): T[] => eqs.map((e) => ({ ...e, descricao: nomeComponente(e) }));
export const calculoLocacao = (v: Pick<Versao, 'locacao'>) => {
  const o = ofertaSelecionada(v);
  return o ? calcularLocacao(o, v.locacao!.base) : undefined;
};
export const calculoTradein = (v: Pick<Versao, 'tradein'>) => {
  const a = avaliacaoSelecionada(v);
  return a ? calcularTradein(a, comNome(v.tradein!.equipamentos)) : undefined;
};
export const linhasResultadoDaVersao = (v: Pick<Versao, 'locacao' | 'tradein' | 'itens'>): (ResultadoLinha & { id: string; quantidade: number })[] => v.locacao?.componentes ?? v.tradein?.equipamentos ?? v.itens;
/** Custo em R$: usa a PTAX da cotação selecionada do item (ou a PTAX padrão da versão). */
export const custoItemReais = (i: { custoUnitMoeda: number; produtoId: string; ptaxUtilizada?: number; produtoCopia?: ProdutoCopia }, d: Pick<Base, 'produtos'>, ptax: number) => {
  const m = produtoDoItem(d, i).moeda;
  return arred(m === 'USD' ? i.custoUnitMoeda * (i.ptaxUtilizada ?? ptax) : i.custoUnitMoeda);
};
export const custoCotacaoReais = (c: Cotacao) => arred(c.moeda === 'USD' ? c.custoUnitMoeda * (ptaxUtilizada(c.ptax) ?? 0) : c.custoUnitMoeda);
/** Linha econômica da versão, igual para os três tipos (valor da operação × custo/repasse). */
export const linhasDaVersao = (d: Pick<Base, 'produtos'>, v: Versao): Linha[] => {
  if (v.locacao) {
    const c = calculoLocacao(v);
    return c ? [{ quantidade: 1, custoUnit: c.liquidoTotal, vendaUnit: c.totalContrato }] : [];
  }
  if (v.tradein) {
    const c = calculoTradein(v);
    return c ? [{ quantidade: 1, custoUnit: c.oferta, vendaUnit: c.bruto }] : [];
  }
  return v.itens.map((i) => ({ quantidade: i.quantidade, custoUnit: custoItemReais(i, d, v.ptax), vendaUnit: i.precoUnit }));
};
export const economiaVersao = (d: Pick<Base, 'produtos'>, v: Versao) => economia(linhasDaVersao(d, v), tipoDaVersao(v) === 'venda' ? v.frete : CIF);
export const parceiroSelecionado = (v: Pick<Versao, 'locacao' | 'tradein'>) => ofertaSelecionada(v)?.entidadeId ?? avaliacaoSelecionada(v)?.entidadeId;
/** Grupos de uma versão: cotados (qualquer cotação) e selecionados (faturadores dos itens ou parceiro selecionado). */
export function gruposDaVersao(d: Pick<Base, 'entidades'>, v: Versao) {
  const g = (entidadeId: string) => entidadeDe(d, entidadeId)?.grupoId;
  const uniq = (xs: (string | undefined)[]) => [...new Set(xs.filter((x): x is string => Boolean(x)))];
  if (v.locacao || v.tradein) {
    const todas = v.locacao?.ofertas ?? v.tradein!.avaliacoes;
    const sel = parceiroSelecionado(v);
    const selecionados = sel ? uniq([g(sel)]) : [];
    return { cotados: uniq([...todas.map((o) => g(o.entidadeId)), ...selecionados]), selecionados };
  }
  const selecionados = uniq(v.itens.filter((i) => i.entidadeId).map((i) => g(i.entidadeId)));
  return { cotados: uniq([...v.cotacoes.map((c) => g(c.entidadeId)), ...selecionados]), selecionados };
}
/** Local do parque do Trade-in (entidade do Cliente ou "Outro local"). */
export function localParqueDaVersao(d: Pick<Base, 'clientes'>, v: Versao): { texto: string; endereco?: Endereco; origem: string } {
  const tri = v.tradein!;
  if (tri.localEntidadeId === 'outro') return { texto: tri.localOutro ? enderecoTexto(tri.localOutro) : '—', endereco: tri.localOutro, origem: 'Outro local informado na Proposta (o endereço oficial não muda)' };
  const e = tri.localEntidadeId ? entidadeClienteDe(d, tri.localEntidadeId)?.entidade : undefined;
  return e ? { texto: `${e.razao} — ${enderecoTexto(e.endereco)}`, endereco: e.endereco, origem: `Endereço oficial da ${e.tipo} (${e.cnpj})` } : { texto: tri.localParque || '—', origem: 'texto livre' };
}
export interface LocalCongelado {
  cnpj: string;
  razao: string;
  endereco: Endereco;
  contato: string;
  telefone: string;
  origem: 'faturamento' | 'mesmo endereço do faturamento' | 'outra entidade cadastrada' | 'endereço específico da operação';
  /**
   * P4 bloco 3 (D2/D3, 17/10/2026): congelados no aceite a partir desta versão — IE e situação da IE da entidade
   * (faturamento; entrega quando é entidade cadastrada) e e-mail do contato escolhido na Proposta (faturamento).
   * Pedidos anteriores não têm estas chaves: a Ordem reconstitui pela auditoria só com evidência (ordens.cadastro.ts).
   */
  ie?: string;
  ieSituacao?: string;
  email?: string;
}
const ENDERECO_VAZIO: Endereco = { cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '' };
/** Resolve faturamento e entrega definidos na Proposta. */
export function locaisDaVersao(d: Pick<Base, 'clientes'>, v: Versao): { faturamento: LocalCongelado; entrega: LocalCongelado } {
  const fat = entidadeClienteDe(d, v.faturamentoEntidadeId)?.entidade;
  const k = contatoDe(d, v.contatoId);
  const faturamento: LocalCongelado = { cnpj: fat?.cnpj ?? '', razao: fat?.razao ?? '—', endereco: fat?.endereco ?? ENDERECO_VAZIO, contato: k?.nome ?? '', telefone: k?.telefone ?? '', origem: 'faturamento', ie: fat?.ie ?? '', ieSituacao: fat?.ieSituacao ?? '', email: k?.email ?? '' };
  let entrega: LocalCongelado;
  if (v.entrega.mesmoFaturamento) entrega = { ...faturamento, contato: v.entrega.contato, telefone: v.entrega.telefone, origem: 'mesmo endereço do faturamento', email: undefined };
  else if (v.entrega.entidadeId) {
    const e = entidadeClienteDe(d, v.entrega.entidadeId)?.entidade;
    entrega = { cnpj: e?.cnpj ?? '', razao: e?.razao ?? '—', endereco: e?.endereco ?? ENDERECO_VAZIO, contato: v.entrega.contato, telefone: v.entrega.telefone, origem: 'outra entidade cadastrada', ie: e?.ie ?? '', ieSituacao: e?.ieSituacao ?? '' };
  } else {
    const esp = v.entrega.especifica ?? { cnpj: faturamento.cnpj, endereco: faturamento.endereco };
    entrega = { cnpj: esp.cnpj, razao: faturamento.razao, endereco: esp.endereco, contato: v.entrega.contato, telefone: v.entrega.telefone, origem: 'endereço específico da operação' };
  }
  return { faturamento, entrega };
}

/** Divisão dos itens por ENTIDADE faturadora (dados de faturamento do PDF; cada uma gera a sua Ordem na B5). */
export function faturadores(d: Pick<Base, 'entidades' | 'grupos'>, v: Versao) {
  const mapa = new Map<string, number[]>();
  v.itens.forEach((i, n) => {
    if (i.entidadeId) mapa.set(i.entidadeId, [...(mapa.get(i.entidadeId) ?? []), n + 1]);
  });
  return [...mapa].flatMap(([entidadeId, itens]) => {
    const entidade = entidadeDe(d, entidadeId);
    const grupo = entidade ? d.grupos.find((g) => g.id === entidade.grupoId) : undefined;
    return entidade && grupo ? [{ entidade, grupo, itens }] : [];
  });
}

/** Dados da INTTRIX nos documentos (P-18): parâmetro `comercial.empresa`, cadastrado pela Administração. */
export interface DadosEmpresa {
  razao: string;
  cnpj: string;
  endereco?: string;
  telefone?: string;
  email?: string;
  site?: string;
}
export interface TermoComercial {
  titulo: string;
  texto: string;
}
/**
 * TERMOS GERAIS da Proposta de Venda (decisões da Valéria, 07/10/2026): parâmetro versionado `comercial.termos_gerais`
 * (Administração → Parâmetros), separado das demais condições. Valor:
 *   `{"atencao"?: "...", "itens": [{"titulo": "...", "texto": "..."}, ...]}` — "atencao" é opcional; cada item tem título
 *   (destacado no PDF) e texto. O formato anterior (itens só com texto) continua aceito, para que versões já congeladas
 *   sigam legíveis. O texto é usado exatamente como cadastrado (o PDF só numera os itens). Rascunho mostra os vigentes;
 *   o ENVIO copia para a versão; versões enviadas sem a cópia não recebem o conteúdo retroativamente.
 */
export const CHAVE_TERMOS_GERAIS = 'comercial.termos_gerais';
export interface ItemTermosGerais {
  titulo: string;
  texto: string;
}
export interface TermosGerais {
  atencao?: string;
  itens: (ItemTermosGerais | string)[];
}
const textoPreenchido = (v: unknown): v is string => typeof v === 'string' && v.trim() !== '';
/** Confere a estrutura (sem alterar o texto). Devolve os termos ou a lista de problemas. */
export function conferirTermosGerais(x: unknown): { termos: TermosGerais } | { problemas: string[] } {
  const problemas: string[] = [];
  if (!x || typeof x !== 'object' || Array.isArray(x)) return { problemas: ['o valor deve ser um objeto {"itens": [{"titulo": "...", "texto": "..."}, ...]} (com "atencao" opcional)'] };
  const o = x as Record<string, unknown>;
  const extras = Object.keys(o).filter((k) => k !== 'atencao' && k !== 'itens');
  if (extras.length) problemas.push(`campos não reconhecidos: ${extras.join(', ')}`);
  if (o.atencao !== undefined && !textoPreenchido(o.atencao)) problemas.push('"atencao", quando informado, deve ser um texto não vazio');
  if (!Array.isArray(o.itens) || !o.itens.length) problemas.push('"itens" deve ser uma lista com ao menos um item');
  else
    o.itens.forEach((i, n) => {
      if (typeof i === 'string') {
        if (!i.trim()) problemas.push(`item ${n + 1}: o texto não pode ser vazio`);
        return;
      }
      if (!i || typeof i !== 'object' || Array.isArray(i)) return void problemas.push(`item ${n + 1}: deve ser {"titulo": "...", "texto": "..."}`);
      const it = i as Record<string, unknown>;
      const sobra = Object.keys(it).filter((k) => k !== 'titulo' && k !== 'texto');
      if (sobra.length) problemas.push(`item ${n + 1}: campos não reconhecidos: ${sobra.join(', ')}`);
      if (!textoPreenchido(it.titulo)) problemas.push(`item ${n + 1}: "titulo" deve ser um texto não vazio`);
      if (!textoPreenchido(it.texto)) problemas.push(`item ${n + 1}: "texto" deve ser um texto não vazio`);
    });
  if (problemas.length) return { problemas };
  const itens = (o.itens as unknown[]).map((i) => (typeof i === 'string' ? i : { titulo: (i as ItemTermosGerais).titulo, texto: (i as ItemTermosGerais).texto }));
  return { termos: o.atencao !== undefined ? { atencao: o.atencao as string, itens } : { itens } };
}

/* ======================= Reajuste da Locação ======================= */
export const reajusteDaOferta = (o: Pick<OfertaLocacao, 'reajuste' | 'reajusteIndice' | 'reajustePeriodicidade'>): Reajuste => {
  if (o.reajuste) return o.reajuste;
  const indices = ['IPCA', 'IGP-M', 'INPC', 'Outro'];
  return o.reajusteIndice?.trim() ? { tipo: 'Índice', indice: indices.includes(o.reajusteIndice) ? o.reajusteIndice : 'Outro', indiceOutro: indices.includes(o.reajusteIndice) ? undefined : o.reajusteIndice, periodicidade: o.reajustePeriodicidade } : { tipo: 'Sem reajuste' };
};
export const reajusteDaLocacao = (loc: Pick<DadosLocacao, 'reajuste' | 'ofertas' | 'selecionadaId'>): Reajuste | undefined => {
  if (loc.reajuste) return loc.reajuste;
  const o = loc.ofertas.find((x) => x.id === loc.selecionadaId);
  return o ? reajusteDaOferta(o) : undefined;
};
const nomeIndice = (r: Reajuste) => (r.indice === 'Outro' ? r.indiceOutro?.trim() || 'outro índice' : r.indice);
export const comReajuste = (r: Reajuste): Pick<OfertaLocacao, 'reajuste' | 'reajusteIndice' | 'reajustePeriodicidade'> => ({ reajuste: r, reajusteIndice: r.tipo === 'Índice' ? (nomeIndice(r) ?? '') : r.tipo === 'Percentual fixo' ? `${r.percentual ?? 0}%` : r.tipo, reajustePeriodicidade: r.periodicidade ?? '' });
export const mesmoReajuste = (a?: Reajuste, b?: Reajuste) => textoReajuste(a) === textoReajuste(b);

/* ======================= Comissão líquida estimada (interna) ======================= */
export const liquidaEstimada = (comissaoBruta: number, abatimentoPct: number) => Math.round((comissaoBruta * (1 - abatimentoPct / 100) + Number.EPSILON) * 100) / 100;

/* ======================= PTAX da proposta (F1-57, P1–P5) ======================= */
export const usaPtaxDaProposta = (v: Pick<Versao, 'ptaxProposta'>) => Boolean(v.ptaxProposta);
export const ptaxDaProposta = (v: Pick<Versao, 'ptaxProposta'>) => ptaxUtilizada(v.ptaxProposta);
const emDolar = (d: Pick<Base, 'produtos'>, i: { produtoId: string; produtoCopia?: ProdutoCopia }) => produtoDoItem(d, i).moeda === 'USD';
export const temItemEmDolar = (v: Pick<Versao, 'itens'>, d: Pick<Base, 'produtos'>) => v.itens.some((i) => emDolar(d, i));
/** PTAX distintas já usadas pelos itens em dólar (modelo anterior): mais de uma = proposta com taxas divergentes (P3). */
export const ptaxesDosItens = (v: Pick<Versao, 'itens'>, d: Pick<Base, 'produtos'>) => [...new Set(v.itens.filter((i) => emDolar(d, i) && typeof i.ptaxUtilizada === 'number' && i.ptaxUtilizada > 0).map((i) => i.ptaxUtilizada!))].sort((a, b) => a - b);
/**
 * Aplica a PTAX da proposta a TODOS os itens em dólar: custo em R$ recalculado; preço pela margem recalculado (P1,
 * depois da prévia e da confirmação na tela); preço fixo mantido e a margem efetiva muda (P2); itens em reais intactos.
 */
export function aplicarPtaxDaProposta(v: Versao, d: Pick<Base, 'produtos'>, ptax: PtaxCotacao): Versao {
  return sincronizarCotacoes({ ...v, ptaxProposta: ptax }, d);
}
/** Prévia do efeito de uma nova PTAX (antes × depois por item em dólar e nos totais), sem alterar nada. */
export function previaPtaxDaProposta(v: Versao, d: Pick<Base, 'produtos'>, valor: number) {
  const depois = aplicarPtaxDaProposta(v, d, { ...(v.ptaxProposta ?? { historico: [] }), manual: { valor, origem: 'Ajuste manual', dataReferencia: '', justificativa: 'prévia', usuario: '', alteradaEm: '' } });
  const linha = (x: Versao, i: ItemProposta) => {
    const custo = custoItemReais(i, d, x.ptax);
    return { custo, preco: i.precoUnit, margem: margemPct(i.precoUnit, custo) };
  };
  const itens = v.itens.flatMap((i, n) => {
    if (!emDolar(d, i)) return [];
    const j = depois.itens[n];
    return [{ id: i.id, n: n + 1, precoFixo: i.precoManual, antes: linha(v, i), depois: linha(depois, j) }];
  });
  const ea = economia(linhasDaVersao(d, v));
  const ed = economia(linhasDaVersao(d, depois));
  return { itens, antes: { total: ea.totalProdutos, bruta: ea.bruta, percentual: ea.percentual }, depois: { total: ed.totalProdutos, bruta: ed.bruta, percentual: ed.percentual } };
}

/* ======================= Cotações (cotacoes.ts) ======================= */
/** Copia para os itens os dados das cotações selecionadas (e recalcula a venda quando não é manual). */
export function sincronizarCotacoes(v: Versao, d: Pick<Base, 'produtos'>): Versao {
  // PTAX da proposta (F1-57): todos os itens em dólar usam a mesma taxa; a da cotação fica só como histórico (P4)
  const unica = usaPtaxDaProposta(v) ? ptaxDaProposta(v) : undefined;
  const preco = <T extends ItemProposta>(n: T): T => (n.precoManual ? n : { ...n, precoUnit: vendaPelaMargem(custoItemReais(n, d, v.ptax), n.margemDesejada) });
  return {
    ...v,
    itens: v.itens.map((i) => {
      const c = v.cotacoes.find((x) => x.id === i.cotacaoId);
      if (!c) {
        const semCot = { ...i, cotacaoId: undefined };
        if (!usaPtaxDaProposta(v) || !emDolar(d, i)) return semCot;
        return preco({ ...semCot, ptaxUtilizada: unica });
      }
      const n = { ...i, entidadeId: c.entidadeId, custoUnitMoeda: c.custoUnitMoeda, prazoDias: c.prazoDias, ptaxUtilizada: c.moeda === 'USD' ? (usaPtaxDaProposta(v) ? unica : ptaxUtilizada(c.ptax)) : undefined };
      return preco(n);
    }),
  };
}
export const registrarCotacao = (v: Versao, texto: string, autor: string, data: string): Registro[] => [...(v.historicoCotacoes ?? []), { data, texto, autor }];

/* ======================= Resultado por linha (resultado.ts) ======================= */
export interface LinhaInformada {
  id: string;
  ganha: number;
  perdida: number;
  motivo?: string;
  observacao?: string;
}
export const RESULTADO_LINHA: Record<Resultado, string> = { aceito: 'Ganho', aceito_parcial: 'Parcial', nao_aceito: 'Perdido', pendente: 'Pendente' };
export function classificar(quantidade: number, ganha: number, perdida: number): Resultado {
  if (ganha === quantidade) return 'aceito';
  if (ganha > 0) return 'aceito_parcial';
  if (perdida === quantidade) return 'nao_aceito';
  return 'pendente';
}
export const linhasIniciais = (v: Versao): LinhaInformada[] => linhasResultadoDaVersao(v).map((l) => ({ id: l.id, ganha: l.quantidadeAceita, perdida: l.quantidadePerdida ?? 0, motivo: l.motivoPerda ?? '', observacao: l.observacaoResultado ?? '' }));
/** Motivo da perda "Outro" exige a observação (encerramento e linhas não ganhas; decisão de 09/10/2026). */
export const motivoPerdaExigeObservacao = (motivo: string) => motivo.trim().toLocaleLowerCase('pt-BR') === 'outro';
export function validarLinhas(v: Versao, linhas: LinhaInformada[]): string[] {
  const erros: string[] = [];
  const ids = new Set(linhasResultadoDaVersao(v).map((l) => l.id));
  for (const x of linhas) if (!ids.has(x.id)) erros.push(`Linha ${x.id}: não existe nesta versão.`);
  for (const l of linhasResultadoDaVersao(v)) {
    const x = linhas.find((y) => y.id === l.id);
    if (!x) continue;
    if (![x.ganha, x.perdida].every((n) => Number.isInteger(n) && n >= 0)) erros.push(`Linha ${l.id}: quantidades devem ser inteiros não negativos.`);
    if (x.ganha > l.quantidade) erros.push(`Linha ${l.id}: quantidade ganha maior que a proposta.`);
    if (x.ganha + x.perdida > l.quantidade) erros.push(`Linha ${l.id}: ganha + perdida passa da quantidade proposta.`);
    if (x.perdida > 0 && !x.motivo?.trim()) erros.push(`Linha ${l.id}: informe o motivo da perda.`);
    else if (x.perdida > 0 && motivoPerdaExigeObservacao(x.motivo ?? '') && !x.observacao?.trim()) erros.push(`Linha ${l.id}: motivo “Outro” exige a observação.`);
  }
  return erros;
}
/** Aplica as linhas à versão (sem mexer em nada mais). */
export function aplicarLinhas(v: Versao, linhas: LinhaInformada[]): Versao {
  const f = <T extends { id: string; quantidade: number }>(l: T): T => {
    const x = linhas.find((y) => y.id === l.id);
    if (!x) return l;
    return { ...l, quantidadeAceita: x.ganha, quantidadePerdida: x.perdida, motivoPerda: x.motivo ?? '', observacaoResultado: x.observacao ?? '', resultado: classificar(l.quantidade, x.ganha, x.perdida) };
  };
  if (v.locacao) return { ...v, locacao: { ...v.locacao, componentes: v.locacao.componentes.map(f) } };
  if (v.tradein) return { ...v, tradein: { ...v.tradein, equipamentos: v.tradein.equipamentos.map(f) } };
  return { ...v, itens: v.itens.map(f) };
}
export function resumoLinhas(v: Versao) {
  const ls = linhasResultadoDaVersao(v);
  const conta = (r: Resultado) => ls.filter((l) => l.resultado === r).length;
  const tudoGanho = ls.length > 0 && ls.every((l) => l.quantidadeAceita === l.quantidade);
  const algumGanho = ls.some((l) => l.quantidadeAceita > 0);
  return { linhas: ls.length, ganhos: conta('aceito'), parciais: conta('aceito_parcial'), perdidos: conta('nao_aceito'), pendentes: conta('pendente'), tudoGanho, algumGanho, conjunto: tipoDaVersao(v) === 'locacao' };
}
export const textoResumo = (r: ReturnType<typeof resumoLinhas>) => `${r.ganhos} ganho(s), ${r.parciais} parcial(is), ${r.perdidos} perdido(s), ${r.pendentes} pendente(s)`;
/** Resultado guardado à parte do conteúdo (a versão emitida nunca muda): mapa linha → quantidades. */
export type MapaResultado = Record<string, { ganha: number; perdida: number; motivo?: string; observacao?: string }>;
export const comResultado = (v: Versao, r: MapaResultado): Versao => aplicarLinhas(v, Object.entries(r).map(([id, x]) => ({ id, ...x })));

/* ======================= Pendências de envio (acoes.ts) ======================= */
/**
 * PTAX da versão. Na DEMO-03 havia uma PTAX padrão fictícia (5,00) para item em USD sem cotação; sem PTAX
 * oficial (P-21), o item em moeda estrangeira precisa de uma COTAÇÃO selecionada com a PTAX informada.
 */
export const errosPtaxVersao = (v: Versao, d?: Pick<Base, 'produtos'>): string[] => {
  if (d && usaPtaxDaProposta(v)) return temItemEmDolar(v, d) && !(Number(ptaxDaProposta(v)) > 0) ? ['Defina a PTAX da proposta em Condições comerciais e frete (consulte a oficial ou informe a manual).'] : [];
  // P3: proposta do modelo anterior com PTAX diferentes entre os itens em dólar — escolha explícita antes do envio
  if (d && ptaxesDosItens(v, d).length > 1) return ['Esta proposta tem PTAX diferentes entre os itens em dólar: escolha a PTAX da proposta em Condições comerciais e frete.'];
  return errosPtaxItens(v, d);
};
const errosPtaxItens = (v: Versao, d?: Pick<Base, 'produtos'>) =>
  v.itens.flatMap((i, n) => {
    if (i.ptaxUtilizada !== undefined && !(i.ptaxUtilizada > 0)) return [`Item ${n + 1}: PTAX obrigatória e maior que zero.`];
    if (!d || produtoDoItem(d, i).moeda !== 'USD') return [];
    if (!i.cotacaoId) return v.ptax > 0 ? [] : [`Item ${n + 1}: produto em USD — selecione uma cotação com a PTAX informada.`];
    return Number(i.ptaxUtilizada) > 0 ? [] : [`Item ${n + 1}: informe a PTAX da cotação selecionada.`];
  });
/**
 * O que falta para ENVIAR a versão ao Cliente — cada pendência diz exatamente o que fazer.
 * O rascunho pode ser salvo com pendências; o envio definitivo, não.
 * `extras`: pendências do servidor que não dependem só do documento (ex.: item em USD sem PTAX; contrato pendente).
 */
export function pendenciasEnvio(v: Versao, d?: Pick<Base, 'produtos'>): string[] {
  const p: string[] = [];
  if (v.locacao) {
    const loc = v.locacao;
    if (!ofertaSelecionada(v)) p.push('Selecione um parceiro de Locação.');
    else p.push(...pendenciasReajusteLocacao(reajusteDaLocacao(loc)));
    if (loc.componentes.length === 0) p.push('Informe ao menos um item.');
    loc.componentes.forEach((c, i) => !c.pn?.trim() && !c.produtoId && p.push(`Componente ${i + 1}: informe o PN/SKU (obrigatório na Locação).`));
    const c = calculoLocacao(v);
    if (c) p.push(...c.erros);
    return p;
  }
  if (v.tradein) {
    const tri = v.tradein;
    const a = avaliacaoSelecionada(v);
    if (!a) p.push('Selecione um parceiro de Trade-in.');
    if (tri.equipamentos.length === 0) p.push('Informe ao menos um item.');
    tri.equipamentos.forEach((e, i) => {
      const faltam = [!e.categoria.trim() && 'Categoria', !e.fabricante.trim() && 'Fabricante', !e.modelo.trim() && 'Modelo'].filter(Boolean);
      if (faltam.length) p.push(`Equipamento ${i + 1}: informe ${faltam.join(', ')}.`);
    });
    const c = calculoTradein(v);
    if (a && c && !(c.oferta > 0)) p.push('Informe os valores destinados ao Cliente.');
    if (c) p.push(...c.erros);
    if (!tri.confirmacaoBancaria) p.push('Confirme os dados bancários (confirmação operacional: os dados seguem fora do HUB).');
    const dias = v.condicoes.prazoRecebimentoDias;
    if (typeof dias !== 'number' || !Number.isInteger(dias) || dias < 0) p.push('Informe o prazo de recebimento do Cliente (dias).');
    if (!MARCOS_RECEBIMENTO.some((m) => m.id === v.condicoes.marcoRecebimento)) p.push('Informe a partir de quando o prazo de recebimento é contado.');
    else if (v.condicoes.marcoRecebimento === 'outro' && !v.condicoes.marcoRecebimentoDescricao?.trim()) p.push('Descreva o marco de contagem do prazo de recebimento (Outro).');
    const mod = tri.modalidadeLogistica;
    if (!mod || !tri.responsavelLogistica || tri.responsavelLogistica === 'A definir' || (mod && !RESPONSAVEIS_POR_MODALIDADE[mod].includes(tri.responsavelLogistica))) p.push('Resolva o responsável pela logística.');
    return [...new Set(p)];
  }
  if (v.itens.length === 0) p.push('Informe ao menos um item.');
  p.push(...errosPtaxVersao(v, d));
  return [...new Set(p)];
}
function pendenciasReajusteLocacao(r?: Reajuste): string[] {
  if (!r) return ['Reajuste: escolha o tipo.'];
  if (r.tipo === 'Índice') return [!r.indice && 'Reajuste: escolha o índice.', r.indice === 'Outro' && !r.indiceOutro?.trim() && 'Reajuste: informe o nome do índice.', !r.periodicidade && 'Reajuste: informe a periodicidade.'].filter(Boolean) as string[];
  if (r.tipo === 'Percentual fixo') return [!(Number(r.percentual) > 0) && 'Reajuste: informe o percentual.', !r.periodicidade && 'Reajuste: informe a periodicidade.'].filter(Boolean) as string[];
  if (r.tipo === 'Outro') return r.descricao?.trim() ? [] : ['Reajuste: descreva a regra (Outro).'];
  return [];
}
export const podeEnviar = (v: Versao, d?: Pick<Base, 'produtos'>) => pendenciasEnvio(v, d).length === 0;

/* ======================= Nova versão (acoes.ts: nova_versao) ======================= */
/** Cópia da versão vigente como novo rascunho: resultados limpos, cópias históricas e abatimento removidos. */
export function novaVersaoDe(base: Versao, numero: number, hoje: string): Versao {
  const copia: Versao = { ...structuredClone(base), numero, situacao: 'rascunho', data: hoje, enviadaEm: undefined, abatimento: undefined, abatimentoNaoConfigurado: undefined };
  delete copia.termos;
  delete copia.empresa;
  delete copia.termosGerais;
  delete copia.recebimentoParceiros;
  const limpa = <T extends { id: string } & ResultadoLinha>(i: T): T => ({ ...i, id: `${i.id.replace(/-v\d+$/, '')}-v${numero}`, resultado: 'pendente', quantidadeAceita: 0, quantidadePerdida: 0, motivoPerda: '', observacaoResultado: '' });
  // Os ids das linhas mudam: as seleções e os valores por linha passam para os novos ids.
  const mapa = new Map<string, string>();
  copia.itens = copia.itens.map((i) => {
    const n = limpa(i);
    mapa.set(i.id, n.id);
    return { ...n, produtoCopia: undefined };
  });
  if (copia.locacao) copia.locacao.componentes = copia.locacao.componentes.map(limpa);
  if (copia.tradein) {
    delete copia.tradein.confirmacaoBancaria; // nova versão: confirmar de novo
    // Pacote 4 (bloco 2): o rascunho novo usa o recebimento padronizado; a versão de origem fica como foi gravada
    copia.condicoes = { ...copia.condicoes, forma: RECEBIMENTO_TRADEIN, condicaoId: '', condicao: '', prazoRecebimentoDias: copia.condicoes.prazoRecebimentoDias ?? null, marcoRecebimento: copia.condicoes.marcoRecebimento ?? null, marcoRecebimentoDescricao: copia.condicoes.marcoRecebimentoDescricao ?? '' };
    copia.tradein.equipamentos = copia.tradein.equipamentos.map((e) => {
      const n = limpa(e);
      mapa.set(e.id, n.id);
      return n;
    });
    copia.tradein.avaliacoes = copia.tradein.avaliacoes.map((a) => ({ ...a, linhas: Object.fromEntries(Object.entries(a.linhas).map(([k, x]) => [mapa.get(k) ?? k, x])) }));
  }
  if (base.validade < hoje) copia.validade = somaDias(hoje, 15);
  return copia;
}
export { somaDias };

/* ======================= Pacote 3 · P3-11 — clonagem ======================= */
const linhaLimpa = <T extends ResultadoLinha>(l: T): T => {
  const { quantidadePerdida: _p, motivoPerda: _m, observacaoResultado: _o, ...resto } = l;
  return { ...(resto as T), resultado: 'pendente', quantidadeAceita: 0 };
};

/**
 * Copia o conteúdo comercial de uma versão de ORIGEM para a versão nova (rascunho) de uma Oportunidade clonada.
 * Não leva resultado, aceite, envio, abatimento, confirmação bancária nem documentos. Não reaplica PTAX: a nova
 * Proposta consulta de novo (a da origem só é citada). Devolve o que precisa de nova confirmação antes do envio.
 */
export function copiaParaClonagem(origem: Versao, nova: Versao): { versao: Versao; reconfirmar: string[] } {
  const reconfirmar: string[] = [];
  const temUsd = origem.cotacoes.some((c) => c.moeda === 'USD');
  if (temUsd) reconfirmar.push('PTAX: consultar novamente (a da origem fica só como referência)');
  reconfirmar.push('Validade: nova data calculada pelo parâmetro');
  const manuais = origem.itens.filter((i) => i.precoManual).length;
  if (manuais) reconfirmar.push(`${manuais} preço(s) manual(is): confirmar o valor`);
  if (origem.cotacoes.length) reconfirmar.push(`${origem.cotacoes.length} cotação(ões): reconfirmar com o fornecedor`);
  const versao: Versao = {
    ...nova,
    contatoId: nova.contatoId,
    frete: { ...origem.frete },
    condicoes: { ...origem.condicoes },
    itens: origem.itens.map((i) => {
      const { ptaxUtilizada: _x, ...semPtax } = linhaLimpa(i);
      return semPtax;
    }),
    cotacoes: origem.cotacoes.map((c) => {
      const { ptax: _x, ...semPtax } = c;
      return semPtax;
    }),
    historicoCotacoes: [{ data: nova.data, texto: 'Cotações copiadas na clonagem: reconfirmar com o fornecedor antes do envio', autor: 'HUB' }],
  };
  if (origem.locacao && nova.locacao) {
    const L = origem.locacao;
    versao.locacao = {
      ...nova.locacao,
      descricao: L.descricao,
      base: L.base,
      formaPagamento: L.formaPagamento,
      implantacao: L.implantacao,
      observacoes: L.observacoes,
      componentes: L.componentes.map(linhaLimpa),
      ofertas: L.ofertas.map((o) => ({ ...o })),
      selecionadaId: '',
      ...(L.reajuste ? { reajuste: L.reajuste } : {}),
      ...(L.responsavelEntrega ? { responsavelEntrega: L.responsavelEntrega } : {}),
      ...(L.responsavelImplantacao ? { responsavelImplantacao: L.responsavelImplantacao } : {}),
    };
    if (L.ofertas.length) reconfirmar.push('Parceiro de Locação: selecionar de novo (a regra de margem e as condições do parceiro são reaplicadas)');
  }
  if (origem.tradein && nova.tradein) {
    const T = origem.tradein;
    const { confirmacaoBancaria: _c, ...semConfirmacao } = T;
    versao.tradein = { ...nova.tradein, ...semConfirmacao, equipamentos: T.equipamentos.map(linhaLimpa), avaliacoes: T.avaliacoes.map((a) => ({ ...a })), selecionadaId: '' };
    reconfirmar.push('Trade-in: selecionar o comprador e confirmar os dados bancários de novo');
  }
  return { versao, reconfirmar };
}
